import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { useHistory } from 'react-router-dom';
import { Button, Fieldset, ErrorSummary } from 'govuk-react-jsx';
import { useSelector, useDispatch } from 'react-redux';
import { addDays, isAfter, isBefore, format, isSameDay } from 'date-fns';
import { DateInput } from 'govuk-react-jsx/govuk/components/date-input';
import {
  getDateOfGrantApplication,
  setDateOfCompletion,
  getDayInstallationDate,
  setDayInstallationDate,
  getMonthInstallationDate,
  setMonthInstallationDate,
  getYearInstallationDate,
  setYearInstallationDate,
  getGrantTypeDynamicWording,
} from '../../features/installer/installerJourneySlice';
import { getWordingTemplates } from '../../firebase';
import {
  getCurrentLanguage,
  getCopyProviderCY,
  getCopyProviderEN,
} from '../../features/app/appSlice';
import useTitle from '../../hooks/useTitle';

const InstallationDate = ({ location }) => {
  const grantTypeDynamicWording = useSelector(getGrantTypeDynamicWording);
  const isFromCheckAnswers = location?.state?.isFromCheckAnswers;
  const history = useHistory();
  const dispatch = useDispatch();
  const dateOfGrantApplicationRedux = useSelector(getDateOfGrantApplication);
  const dayInstallationDateRedux = useSelector(getDayInstallationDate);
  const monthInstallationDateRedux = useSelector(getMonthInstallationDate);
  const yearInstallationDateRedux = useSelector(getYearInstallationDate);
  const currentLanguage = useSelector(getCurrentLanguage);
  const copyProviderCY = useSelector(getCopyProviderCY);
  const copyProviderEN = useSelector(getCopyProviderEN);
  const copyProvider =
    currentLanguage === 'en' ? copyProviderEN : copyProviderCY;
  const [dynamicWording, setDynamicWording] = useState({
    message: null,
    is_dynamic: false,
  });
  const [isLoading, setIsLoading] = useState(true);

  useTitle(
    'installer.DateOfCompletion.s18',
    'Enter the date you completed this installation - OZEV Electric Vehicle Chargepoint Grant - GOV.UK'
  );

  const [errorMsg, setErrorMsg] = useState(null);
  const [hasDayError, setHasDayError] = useState(false);
  const [hasMonthError, setHasMonthError] = useState(false);
  const [hasYearError, setHasYearError] = useState(false);
  const [errorList, setErrorList] = useState([]);
  const getMessage = () => {
    // Before activation → Show hardcoded

    if (!dynamicWording.is_dynamic) {
      return copyProvider.getCopy(
        'installer.DateOfCompletion.s17',
        'Installation must have been completed within 180 days of the application'
      );
    }

    // After activation:

    const message =
      currentLanguage === 'en'
        ? dynamicWording.message.applicationSubmition180_Landlordmessage_en
        : dynamicWording.message.applicationSubmition180_Landlordmessage_cy;

    if (message === '') {
      return '';
    }

    // If null or undefined → Show hardcoded
    if (message === null || message === undefined) {
      return copyProvider.getCopy(
        'installer.DateOfCompletion.s17',
        'Installation must have been completed within 180 days of the application'
      );
    }

    // If has value → Show dynamic tex;
    return message;
  };
  // Error messages
  const dayZeroErrorMsg = copyProvider.getCopy(
    'installer.DateOfCompletion.s12',
    'Enter a valid day'
  );
  const dayBlankErrorMsg = copyProvider.getCopy(
    'installer.DateOfCompletion.s0',
    'Enter a day'
  );
  const monthZeroErrorMsg = copyProvider.getCopy(
    'installer.DateOfCompletion.s13',
    'Enter a valid month'
  );
  const monthBlankErrorMsg = copyProvider.getCopy(
    'installer.DateOfCompletion.s0',
    'Enter a month'
  );

  const yearBlankErrorMsg = copyProvider.getCopy(
    'installer.DateOfCompletion.s10',
    'Enter a year'
  );
  const yearZeroErrorMsg = copyProvider.getCopy(
    'installer.DateOfCompletion.s14',
    'Enter a valid year'
  );
  const combinedBlankErrorMsg = copyProvider.getCopy(
    'installer.DateOfCompletion.s11',
    'Enter a date'
  );
  const futureDateInputErrorMsg = copyProvider.getCopy(
    'installer.DateOfCompletion.s15',
    "Enter today's date or a date in the past"
  );
  const beforeGrantApplicationDateInputErrorMsg = copyProvider.getCopy(
    'installer.DateOfCompletion.s16',
    'Installation must have been completed after the date of the application'
  );
  const dateRangeOutErrorMsg = getMessage();

  const handleContinueButton = () => {
    // Day error messages
    const showDayZeroError = {
      children: dayZeroErrorMsg,
    };

    const showDayBlankError = {
      children: dayBlankErrorMsg,
    };

    // Month error messages
    const showMonthZeroError = {
      children: monthZeroErrorMsg,
    };

    const showMonthBlankError = {
      children: monthBlankErrorMsg,
    };

    // Year error messages
    const showYearBlankError = {
      children: yearBlankErrorMsg,
    };
    const showYearZeroError = {
      children: yearZeroErrorMsg,
    };

    // Blank error message
    const showCombinedBlankError = {
      children: combinedBlankErrorMsg,
    };

    // Future date input error message
    const showFutureDateError = {
      children: futureDateInputErrorMsg,
    };

    // Before Grant application error message
    const showBeforeGrantApplicationError = {
      children: beforeGrantApplicationDateInputErrorMsg,
    };

    // 180 day range error message
    const showDateRangeError = {
      children: dateRangeOutErrorMsg,
    };

    // If day is not between 1 and 31 => 'Enter a valid day' error
    if (
      dayInstallationDateRedux === 0 ||
      dayInstallationDateRedux === null ||
      dayInstallationDateRedux > 31
    ) {
      setErrorMsg(showDayZeroError);
      setHasDayError(true);
      setHasMonthError(false);
      setHasYearError(false);
      setErrorList([{ ...showDayZeroError, href: '#day' }]);
      // If day, month or year is empty => 'Enter a date' error
    } else if (
      !dayInstallationDateRedux &&
      !monthInstallationDateRedux &&
      !yearInstallationDateRedux
    ) {
      setErrorMsg(showCombinedBlankError);
      setHasDayError(true);
      setHasMonthError(true);
      setHasYearError(true);
      setErrorList([{ ...showCombinedBlankError, href: '#date-input' }]);
      // If month is not between 1 and 12 => 'Enter a valid month' error
    } else if (
      monthInstallationDateRedux === 0 ||
      monthInstallationDateRedux === null ||
      monthInstallationDateRedux > 12
    ) {
      setErrorMsg(showMonthZeroError);
      setHasMonthError(true);
      setHasDayError(false);
      setHasYearError(false);
      setErrorList([{ ...showMonthZeroError, href: '#month' }]);
      // TODO: refactor this!
      // If month and year are empty => 'Enter a date' error
    } else if (!monthInstallationDateRedux && !yearInstallationDateRedux) {
      setErrorMsg(showCombinedBlankError);
      setHasDayError(false);
      setHasMonthError(true);
      setHasYearError(true);
      setErrorList([{ ...showCombinedBlankError, href: '#date-input' }]);
      // TODO: refactor this!
      // If day or year are empty => 'Enter a date' error
    } else if (!dayInstallationDateRedux && !yearInstallationDateRedux) {
      setErrorMsg(showCombinedBlankError);
      setHasDayError(true);
      setHasMonthError(false);
      setHasYearError(true);
      setErrorList([{ ...showCombinedBlankError, href: '#date-input' }]);
      // TODO: refactor this!
      // If day or month are empty => 'Enter a date' error
    } else if (!dayInstallationDateRedux && !monthInstallationDateRedux) {
      setErrorMsg(showCombinedBlankError);
      setHasDayError(true);
      setHasMonthError(true);
      setHasYearError(false);
      setErrorList([{ ...showCombinedBlankError, href: '#date-input' }]);
      // If month is empty => 'Enter a month' error
    } else if (monthInstallationDateRedux === '') {
      setErrorMsg(showMonthBlankError);
      setHasDayError(false);
      setHasMonthError(true);
      setHasYearError(false);
      setErrorList([{ ...showMonthBlankError, href: '#month' }]);
      // If month is other than a number => 'Enter a valid day' error
    } else if (
      !monthInstallationDateRedux < 12 &&
      !monthInstallationDateRedux > 0
    ) {
      setErrorMsg(showMonthZeroError);
      setHasDayError(false);
      setHasMonthError(true);
      setHasYearError(false);
      setErrorList([{ ...showMonthZeroError, href: '#month' }]);
      // TODO: refactor all this! we could just use a regex here!
      // If year is empty => 'Enter a year' error
    } else if (yearInstallationDateRedux === '') {
      setErrorMsg(showYearBlankError);
      setHasYearError(true);
      setHasMonthError(false);
      setHasDayError(false);
      setErrorList([{ ...showYearBlankError, href: '#year' }]);
      // If year is other than a number => 'Enter a valid day' error
    } else if (
      !yearInstallationDateRedux < 9999 &&
      !yearInstallationDateRedux > 0
    ) {
      setErrorMsg(showYearZeroError);
      setHasDayError(false);
      setHasMonthError(false);
      setHasYearError(true);
      setErrorList([{ ...showYearZeroError, href: '#year' }]);
      // If year is not 4 numeric characters => 'Enter a valid year' error
    } else if (
      yearInstallationDateRedux < 999 ||
      yearInstallationDateRedux > 9999
    ) {
      setErrorMsg(showYearZeroError);
      setHasDayError(false);
      setHasMonthError(false);
      setHasYearError(true);
      setErrorList([{ ...showYearZeroError, href: '#year' }]);
      // If day is empty => 'Enter a day' error
    } else if (dayInstallationDateRedux === '') {
      setErrorMsg(showDayBlankError);
      setHasDayError(true);
      setHasMonthError(false);
      setHasYearError(false);
      setErrorList([{ ...showDayBlankError, href: '#day' }]);
      // If day is other than a number => 'Enter a valid day' error
    } else if (
      !dayInstallationDateRedux < 31 &&
      !dayInstallationDateRedux > 0
    ) {
      setErrorMsg(showDayZeroError);
      setHasDayError(true);
      setHasMonthError(false);
      setHasYearError(false);
      setErrorList([{ ...showDayZeroError, href: '#day' }]);
      // If day is greater than 30 AND month is (4,6,9,11) => 'Enter a valid day' error
    } else if (
      dayInstallationDateRedux > 30 &&
      (monthInstallationDateRedux === 4 ||
        monthInstallationDateRedux === 6 ||
        monthInstallationDateRedux === 9 ||
        monthInstallationDateRedux === 11)
    ) {
      setErrorMsg(showDayZeroError);
      setHasDayError(true);
      setHasMonthError(false);
      setHasYearError(false);
      setErrorList([{ ...showDayZeroError, href: '#day' }]);
      // If day is greater than 29 of Leap year Febuary => 'Enter a valid day' error
    } else if (
      dayInstallationDateRedux > 29 &&
      monthInstallationDateRedux === 2 &&
      ((yearInstallationDateRedux % 4 === 0 &&
        yearInstallationDateRedux % 100 !== 0) ||
        yearInstallationDateRedux % 400 === 0)
    ) {
      setErrorMsg(showDayZeroError);
      setHasYearError(false);
      setHasMonthError(false);
      setHasDayError(true);
      setErrorList([{ ...showDayZeroError, href: '#day' }]);
      // If day is greater than 28 of non leap year => 'Enter a valid day' error
    } else if (
      dayInstallationDateRedux > 28 &&
      monthInstallationDateRedux === 2 &&
      ((!yearInstallationDateRedux % 4 === 0 &&
        !yearInstallationDateRedux % 100 !== 0) ||
        !(yearInstallationDateRedux % 400 === 0))
    ) {
      setErrorMsg(showDayZeroError);
      setHasYearError(false);
      setHasDayError(true);
      setHasMonthError(false);
      setErrorList([{ ...showDayZeroError, href: '#day' }]);
      // Following conditions to check for applications of future dates and dates out of 180 day range
    } else {
      const dateOfCompletion = new Date(
        yearInstallationDateRedux,
        monthInstallationDateRedux - 1,
        dayInstallationDateRedux
      );

      const todaysDate = new Date();
      const schemeEndDate = new Date(2026, 4, 26, 23, 59, 59);
      const expiryDate180 = addDays(new Date(dateOfGrantApplicationRedux), 180);
      const expiryDate = isBefore(schemeEndDate, expiryDate180)
        ? schemeEndDate
        : expiryDate180;

      // if date of completion is before expiryDate
      if (isBefore(dateOfCompletion, expiryDate)) {
        if (
          isBefore(dateOfCompletion, dateOfGrantApplicationRedux) &&
          !isSameDay(dateOfCompletion, dateOfGrantApplicationRedux)
        ) {
          setErrorMsg(showBeforeGrantApplicationError);
          setHasDayError(true);
          setHasMonthError(true);
          setHasYearError(true);
          setErrorList([
            {
              ...showBeforeGrantApplicationError,
              href: '#date-input',
            },
          ]);
        } else if (isAfter(dateOfCompletion, todaysDate)) {
          setErrorMsg(showFutureDateError);
          setHasDayError(true);
          setHasMonthError(true);
          setHasYearError(true);
          setErrorList([
            {
              ...showFutureDateError,
              href: '#date-input',
            },
          ]);
        } else {
          window.scrollTo(0, 0);
          if (isFromCheckAnswers) {
            history.push('/installer/check-your-answers');
          } else {
            history.push('/installer/chargepoints-details');
          }
        }
      } else {
        setErrorMsg(showDateRangeError);
        setHasDayError(true);
        setHasMonthError(true);
        setHasYearError(true);
        setErrorList([
          {
            ...showDateRangeError,
            href: '#date-input',
          },
        ]);
      }

      // Converts date into string format to store inside redux
      const convertedInputDateOfCompletion = format(
        new Date(
          yearInstallationDateRedux,
          monthInstallationDateRedux - 1,
          dayInstallationDateRedux
        ),
        'dd MMMM yyyy'
      );
      dispatch(setDateOfCompletion(convertedInputDateOfCompletion));
    }
  };

  // Onchange functions
  const handleDay = (event) => {
    const inputValue = Number(event.target.value);
    dispatch(setDayInstallationDate(inputValue));
    // setDay(inputValue);
  };

  const handleMonth = (event) => {
    const inputValue = Number(event.target.value);
    // setMonth(inputValue);
    dispatch(setMonthInstallationDate(inputValue));
  };

  const handleYear = (event) => {
    const inputValue = Number(event.target.value);
    dispatch(setYearInstallationDate(inputValue));
    // setYear(inputValue);
  };

  // DateInput class section without error's
  const dayMonthClass = 'govuk-input--width-2';
  const yearClass = 'govuk-input--width-4';

  // DateInput classes with error's
  const dayMonthClassWithError = 'govuk-input--width-2 govuk-input--error';
  const yearClassWithError = 'govuk-input--width-4 govuk-input--error';
  useEffect(() => {
    const fetchWordingTemplates = async () => {
      try {
        setIsLoading(true);
        const response = await getWordingTemplates({
          grantType: grantTypeDynamicWording,
        });

        if (response.data?.success && response.data?.data) {
          setDynamicWording({
            message: response.data.data,
            is_dynamic: response.data.data.is_dynamic,
          });
        }
      } catch (error) {
        console.error('Error fetching wording templates:', error);
      } finally {
        setIsLoading(false); // ✅ End loading
      }
    };

    fetchWordingTemplates(); // ✅ Call the async function
  }, [currentLanguage]);
  if (isLoading) {
    return (
      <div className="govuk-grid-row">
        <p className="govuk-body">Loading...</p>
      </div>
    );
  }
  const pageModel = (
    <div>
      {errorList.length > 0 && (
        <ErrorSummary
          errorList={errorList}
          titleChildren={copyProvider.getCopy(
            'Common.error-summery',
            'There is a problem'
          )}
        />
      )}
      <Fieldset
        legend={{
          children: (
            <h1 className="govuk-heading-xl">
              {copyProvider.getCopy(
                'installer.DateOfCompletion.s1',
                'Enter the date you completed this installation'
              )}
            </h1>
          ),
          className: 'govuk-fieldset__legend--xl',
          isPageHeading: true,
        }}
      >
        <div>
          <DateInput
            inputMode="numeric"
            errorMessage={errorMsg || null}
            fieldset={{
              legend: {
                children: '',
              },
            }}
            hint={{
              children: copyProvider.getCopy(
                'installer.DateOfCompletion.s3',
                'For example, 27 3 2021'
              ),
            }}
            id="date-input"
            items={[
              {
                className: hasDayError ? dayMonthClassWithError : dayMonthClass,
                name: copyProvider.getCopy(
                  'installer.DateOfCompletion.s4',
                  'Day'
                ),
                value: dayInstallationDateRedux || '',
                onChange: handleDay,
                id: 'day',
              },
              {
                className: hasMonthError
                  ? dayMonthClassWithError
                  : dayMonthClass,
                name: copyProvider.getCopy(
                  'installer.DateOfCompletion.s5',
                  'Month'
                ),
                value: monthInstallationDateRedux || '',
                onChange: handleMonth,
                id: 'month',
              },
              {
                className: hasYearError ? yearClassWithError : yearClass,
                name: copyProvider.getCopy(
                  'installer.DateOfCompletion.s6',
                  'Year'
                ),
                value: yearInstallationDateRedux || '',
                onChange: handleYear,
                id: 'year',
              },
            ]}
          />
        </div>
      </Fieldset>

      <div>
        <Button className="govuk-button" onClick={handleContinueButton}>
          {copyProvider.getCopy('installer.DateOfCompletion.s7', 'Continue')}
        </Button>
      </div>
    </div>
  );
  return pageModel;
};
InstallationDate.propTypes = {
  location: PropTypes.shape({
    state: PropTypes.shape({
      isFromCheckAnswers: PropTypes.bool,
    }),
  }),
};

InstallationDate.defaultProps = {
  location: {
    state: {
      isFromCheckAnswers: false,
    },
  },
};
export default InstallationDate;
